import { parseDay } from '@map-my-money/engine';
import { useCallback, useId, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ForecastChart, forecastChartLabel } from '../components/charts/ForecastChart.tsx';
import { ChatBanner, ResultStamp } from '../components/ChatLink.tsx';
import { CheckInDialog } from '../components/CheckInDialog.tsx';
import {
  AnchorLine, DebtDates, GoalStatus, LogWaypointCard, NorthStarCard, OwnedList, RecentChanges, RestOfMonthPill, ThisMonthCard,
  WaypointsHeader, useWaypointSummary,
} from '../components/cards.tsx';
import { ALL_SERIES, SeriesChips, TimeframePicker, toggleSeries } from '../components/forecastControls.tsx';
import { AskAssistant, AssistantActivity } from '../components/Assistant.tsx';
import { Icon } from '../components/Icon.tsx';
import { firstName, PageHeader } from '../components/Shell.tsx';
import { SyncStatus } from '../components/SyncStatus.tsx';
import { Delta, Ring, SectionHeader } from '../components/ui.tsx';
import { t } from '../i18n/index.ts';
import { now, todayDay } from '../lib/clock.ts';
import { compactMoney, longWeekday, money } from '../lib/format.ts';
import { useForecastView, useHomeData, useIsDesktop, useLocalPreference } from '../lib/hooks.ts';
import { overview, type CurrentState, type ForecastView, type SeriesKey } from '../lib/model.ts';
import { useActivePlan, useStore } from '../state/store.tsx';
import { DraftBanner } from '../components/DraftBanner.tsx';
import { RateChangesNote } from '../components/RateChangesNote.tsx';
import { WelcomeNote } from '../components/WelcomeNote.tsx';
import { FinishMap } from '../components/FinishMap.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

function greeting(name: string): string {
  const hour = now().getHours();
  if (hour < 12) return name ? t('greeting.morning', { name }) : t('greeting.morningPlain');
  if (hour < 18) return name ? t('greeting.afternoon', { name }) : t('greeting.afternoonPlain');
  return name ? t('greeting.evening', { name }) : t('greeting.eveningPlain');
}

/** The overview at another day, for the chat banner's check of a link's result hash. */
function useOverviewAt() {
  const { plan, waypoints } = useStore();
  return useCallback((asOf: string) => (plan ? overview(plan, waypoints, parseDay(asOf)) : null), [plan, waypoints]);
}

export function HomeScreen() {
  useDocumentTitle(t('nav.home'));
  const desktop = useIsDesktop();
  return desktop ? <DesktopHome /> : <MobileHome />;
}

function DesktopHome() {
  const { me } = useStore();
  const data = useHomeData();
  const [years, setYears] = useLocalPreference('mm.home.years', data?.view.years ?? 10);
  const [visible, setVisible] = useState<ReadonlySet<SeriesKey>>(ALL_SERIES);
  const [logging, setLogging] = useState(false);
  const { canEdit } = useActivePlan();
  const chartView = useForecastView(years);
  const heroId = useId();
  const forecastId = useId();
  const railId = useId();
  const overviewAt = useOverviewAt();
  if (!data || !chartView) return null;
  const { view, month, restOfMonth, assets, planned } = data;
  const name = firstName(me?.user.displayName);
  const first = chartView.rows[0];
  const last = chartView.rows[chartView.rows.length - 1];
  const growth = chartView.finalNetWorth - view.current.netWorth;

  return (
    <div className="page home-desktop">
      <PageHeader
        eyebrow={longWeekday(todayDay())}
        title={greeting(name)}
        actions={
          <>
            <SyncStatus />
            <button type="button" className="button button--primary button--glow" disabled={!canEdit} onClick={() => setLogging(true)}>
              <Icon name="check" size={16} strokeWidth={2.5} />
              {t('home.logWaypoint')}
            </button>
          </>
        }
      />
      <WelcomeNote className="home-desktop__banner" />
      <FinishMap className="home-desktop__banner" onCheckIn={() => setLogging(true)} />
      <DraftBanner className="home-desktop__banner" />
      <RateChangesNote className="home-desktop__banner" />
      <ChatBanner className="home-desktop__banner" recompute={overviewAt} />
      <div className="home-desktop__main">
        <div className="home-top">
          <section className="card card--bento hero-card" aria-labelledby={heroId}>
            <h2 className="eyebrow" id={heroId}>{t('home.netWorth')}</h2>
            <span className="hero-amount hero-amount--lg">{money(view.current.netWorth)}</span>
            <AnchorLine anchor={data.overview.anchor} onCheckIn={canEdit ? () => setLogging(true) : null} />
            <RestOfMonthPill estimate={restOfMonth} monthEnd={data.overview.month_end} />
            <NetWorthBreakdown current={view.current} />
          </section>
          <ThisMonthCard month={month} endCash={Number(data.overview.month_end.cash)} variant="compact" />
        </div>
        <section className="card card--bento home-forecast" aria-labelledby={forecastId}>
          <div className="home-forecast__top">
            <div className="hero-stat">
              <h2 className="eyebrow" id={forecastId}>{t('home.projectedIn', { years })}</h2>
              <span className="hero-amount hero-amount--md text-cyber">{money(chartView.finalNetWorth)}</span>
              <span className="hero-growth">
                <Delta value={growth}>{compactMoney(growth, { sign: true })}</Delta>
                <span className="muted"> {t('home.fromNow', { amount: compactMoney(view.current.netWorth) })}</span>
              </span>
            </div>
            <div className="home-forecast__controls">
              <TimeframePicker value={years} onChange={setYears} size="sm" />
              <SeriesChips visible={visible} onToggle={(k) => setVisible((v) => toggleSeries(v, k))} />
            </div>
          </div>
          {first && last && (
            <ForecastChart
              rows={chartView.rows}
              visible={visible}
              milestones={chartView.milestones}
              height={300}
              label={forecastChartLabel(chartView.rows)}
              now={view.current.netWorth}
            />
          )}
        </section>
        <AssistantActivity />
        <AskAssistant view="home" />
        <ResultStamp result={data.overview} />
      </div>
      <aside className="home-desktop__rail" aria-labelledby={railId}>
        <h2 className="sr-only" id={railId}>{t('home.recentChanges')}</h2>
        <NorthStarCard view={view} level="h3" />
        <DebtDates debtFree={data.overview.debt_free_date} mortgageFree={data.overview.mortgage_free_date} level="h3" />
        <LogWaypointCard current={view.current} onOpen={() => setLogging(true)} showCount={false} />
        <section className="card card--pad-lg">
          <SectionHeader title={t('home.whatYouOwn')} />
          <OwnedList assets={assets} planned={planned} variant="rows" />
        </section>
        <GoalsMini view={view} />
        <section className="card card--pad-lg card--grow">
          <SectionHeader title={t('home.recentChanges')} action={<Link to="/connections" className="text-link">{t('common.history')}</Link>} />
          <RecentChanges limit={4} />
        </section>
      </aside>
      {logging && <CheckInDialog onClose={() => setLogging(false)} />}
    </div>
  );
}

/** What net worth is made of today, each part in its semantic colour. */
function NetWorthBreakdown({ current }: { current: CurrentState }) {
  const rows = [
    { key: 'cash', label: t('home.breakdown.cash'), value: current.cash, tone: 'cyber' },
    { key: 'investments', label: t('home.breakdown.investments'), value: current.investments, tone: 'violet' },
    { key: 'property', label: t('home.breakdown.property'), value: current.propertyEquity, tone: 'mint' },
    { key: 'assets', label: t('home.breakdown.assets'), value: current.assetValue, tone: 'violet' },
    { key: 'debt', label: t('home.breakdown.debt'), value: -current.debt, tone: 'terracotta' },
  ].filter((r) => Math.round(r.value) !== 0);
  return (
    <dl className="breakdown">
      {rows.map((r) => (
        <div key={r.key} className="breakdown__row">
          <dt><span className={`dot dot--${r.tone}`} aria-hidden="true" />{r.label}</dt>
          <dd>{money(r.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The goals after the North Star, compactly, so the rail tells the whole journey. */
function GoalsMini({ view }: { view: ForecastView }) {
  const others = [...view.goals].sort((a, b) => a.goal.priority - b.goal.priority).slice(1);
  if (others.length === 0) return null;
  return (
    <section className="card card--pad-lg">
      <SectionHeader title={t('home.nextGoals')} action={<Link to="/goals" className="text-link">{t('nav.goals')}</Link>} />
      <ul className="goals-mini">
        {others.map((g) => (
          <li key={g.goal.id} className="goals-mini__item">
            <span className="goals-mini__text">
              <span className="goals-mini__label">{g.goal.label}</span>
              <span className="goals-mini__meta"><GoalStatus goal={g} /></span>
            </span>
            <Ring percent={g.progressPercent} tone={GOAL_RING[g.goal.series] ?? 'cyber'} size={36} />
          </li>
        ))}
      </ul>
    </section>
  );
}

const GOAL_RING: Record<string, 'cyber' | 'violet' | 'terracotta' | 'mint'> = { netWorth: 'cyber', investment: 'violet', debt: 'terracotta', cash: 'mint', passive: 'mint' };

function MobileHome() {
  const data = useHomeData();
  const { canEdit } = useActivePlan();
  const [logging, setLogging] = useState(false);
  const wp = useWaypointSummary(data?.view.current ?? { netWorth: 0, cash: 0, investments: 0, debt: 0, propertyEquity: 0, assetValue: 0 });
  const ownedId = useId();
  const netWorthLabel = useMemo(() => t('home.netWorth'), []);
  const overviewAt = useOverviewAt();
  if (!data) return null;
  const { view, month, restOfMonth, assets, planned } = data;
  return (
    <div className="page page--mobile">
      <header className="mobile-header">
        <h1 className="mobile-title">{t('home.title')}</h1>
        <Link to="/what-if" className="fab" aria-label={t('home.quickAction')}>
          <Icon name="bolt" size={18} strokeWidth={2.2} />
        </Link>
      </header>
      <WelcomeNote />
      <FinishMap onCheckIn={() => setLogging(true)} />
      <DraftBanner />
      <RateChangesNote />
      <ChatBanner recompute={overviewAt} />
      <section className="mobile-hero" aria-label={netWorthLabel}>
        <span className="eyebrow">{netWorthLabel}</span>
        <span className="hero-amount hero-amount--lg">{money(view.current.netWorth)}</span>
        <AnchorLine anchor={data.overview.anchor} onCheckIn={canEdit ? () => setLogging(true) : null} />
        <RestOfMonthPill estimate={restOfMonth} monthEnd={data.overview.month_end} />
      </section>
      <ThisMonthCard month={month} endCash={Number(data.overview.month_end.cash)} />
      <section className="stack-sm" aria-label={t('home.waypoints')}>
        <WaypointsHeader count={wp?.count ?? 0} />
        <LogWaypointCard current={view.current} onOpen={() => setLogging(true)} />
      </section>
      <NorthStarCard view={view} />
      <DebtDates debtFree={data.overview.debt_free_date} mortgageFree={data.overview.mortgage_free_date} />
      <section className="stack-sm" aria-labelledby={ownedId}>
        <div className="section-header">
          <h2 className="eyebrow" id={ownedId}>{t('home.whatYouOwn')}</h2>
          <Link to="/plan?add=asset" className="icon-chip icon-chip--violet" aria-label={t('home.whatYouOwn.addAsset')}>
            <Icon name="plus" size={13} strokeWidth={2.5} />
          </Link>
        </div>
        <OwnedList assets={assets} planned={planned} variant="cards" />
      </section>
      <AssistantActivity />
      <AskAssistant view="home" />
      <ResultStamp result={data.overview} />
      {logging && <CheckInDialog onClose={() => setLogging(false)} />}
    </div>
  );
}
